import { Controller } from "@hotwired/stimulus";
import Decimal from "decimal.js";
import type { CurrencyInputElement } from "../elements/currency_input";
import { parseDecimalAmount } from "../utils/parse_decimal_amount";

const DRAG_COMMIT_DISTANCE = 96;
const GESTURE_HINT = "Drag or tap a group to choose a category";

interface Portion { category_id: string; amount: string; tags: string[] | null; snapshot_key?: string; draft?: string; fresh?: boolean; key?: number }
interface Card {
  id: string; revision: string; undo?: string; description: string; date: string;
  amount: string; currency: string; account_amount: string;
  account_currency: string; account_name: string; portions: Portion[];
  accountDraft?: string; accountOverride?: string;
}
interface Gesture { pointer: number; surface: HTMLElement; visual: HTMLElement; previewSurface?: HTMLElement; previewLabel?: HTMLElement; portion: number; x: number; y: number; dx: number; dy: number }
type Status = "loading" | "loading-next" | "ready" | "draft" | "saving" | "saved" | "restoring" | "restored" | "error";
interface ReadBlock { id: string; code: string; error: string }
class QuickReadError extends Error {
  constructor(message: string, readonly code: string) { super(message); }
}

export default class extends Controller<HTMLElement> {
  static targets = ["scope", "count", "status", "feedback", "working", "reload", "continue", "card", "description", "total", "accountTotal", "accountInput", "accountCurrency", "source", "metadata", "portions", "rowTemplate", "controls", "directions", "active", "gestureHint", "editor", "completion", "completionTitle", "undo", "undoEditor", "editorDialog", "editorContent", "editorStatus"];
  static values = { url: String, names: Object, palette: Object, pending: String };
  declare scopeTarget: HTMLElement;
  declare countTarget: HTMLElement;
  declare statusTarget: HTMLElement;
  declare feedbackTarget: HTMLElement;
  declare workingTarget: HTMLElement;
  declare reloadTarget: HTMLButtonElement;
  declare continueTarget: HTMLButtonElement;
  declare cardTarget: HTMLElement;
  declare descriptionTarget: HTMLElement;
  declare totalTarget: HTMLElement;
  declare accountTotalTarget: HTMLElement;
  declare accountInputTarget: HTMLInputElement;
  declare accountCurrencyTarget: HTMLElement;
  declare sourceTarget: HTMLElement;
  declare metadataTarget: HTMLElement;
  declare portionsTarget: HTMLElement;
  declare rowTemplateTarget: HTMLTemplateElement;
  declare controlsTarget: HTMLElement;
  declare directionsTarget: HTMLElement;
  declare activeTarget: HTMLElement;
  declare gestureHintTarget: HTMLElement;
  declare editorTarget: HTMLAnchorElement;
  declare completionTarget: HTMLElement;
  declare completionTitleTarget: HTMLElement;
  declare undoTarget: HTMLButtonElement;
  declare undoEditorTarget: HTMLAnchorElement;
  declare editorDialogTarget: HTMLDialogElement;
  declare editorContentTarget: HTMLElement;
  declare editorStatusTarget: HTMLElement;
  declare urlValue: string;
  declare namesValue: Record<string, string>;
  declare paletteValue: Record<string, { family: string; shade: number }>;
  declare pendingValue: string;

  private current?: Card;
  private confirmed?: Card;
  private undoStep?: { id: string; revision: string; receipt: string; description: string };
  private undoing = false;
  private uncertainUndo?: string;
  private uncertainEditor?: string;
  private editorBefore?: Card;
  private saving = false;
  private flight?: Promise<void>;
  private feedback = "Saved";
  private active = 0;
  private nextPortionKey = 0;
  private busy = false;
  private uncertain = false;
  private readBlocked?: ReadBlock;
  private skipped = new Set<string>();
  private gesture?: Gesture;
  private gestureFrame?: number;
  private previews = new Set<HTMLElement>();
  private cardArrival?: Animation;
  private suppressClick = false;
  private opener?: HTMLElement;
  private cleanup?: AbortController;

  connect() {
    this.cleanup = new AbortController();
    const signal = this.cleanup.signal;
    this.element.addEventListener("click", (event) => {
      const link = (event.target as Element).closest<HTMLAnchorElement>("[data-quick-leave]");
      if (link && (this.busy || this.hasDrafts() || this.changed())) {
        event.preventDefault();
        void this.leave(link.href);
      }
    }, { capture: true, signal });
    this.editorContentTarget.addEventListener("submit", (event) => {
      event.preventDefault();
      event.stopPropagation(); // The deck owns this form response, not Turbo navigation.
      void this.saveEditor(event.target as HTMLFormElement);
    }, { capture: true, signal });
    document.addEventListener("turbo:before-visit", (event) => { if (this.editorDialogTarget.open || this.busy || this.hasDrafts() || this.changed()) event.preventDefault(); }, { signal });
    window.addEventListener("beforeunload", (event) => { if (this.editorDialogTarget.open || this.busy || this.hasDrafts() || this.changed()) { event.preventDefault(); event.returnValue = ""; } }, { signal });
    void this.loadHead();
  }

  disconnect() {
    this.cleanup?.abort();
    this.resetGesture();
    this.cardArrival?.cancel();
    for (const preview of this.previews) preview.remove();
    this.previews.clear();
  }

  private get connected() { return this.element.isConnected && !this.cleanup?.signal.aborted; }
  private name(portion: Portion) { return this.namesValue[portion.category_id] ?? "Category unavailable"; }
  private pending(portion: Portion) { return portion.category_id === this.pendingValue && Number(portion.amount) !== 0; }
  private endpoint(id: string) { return `/categorisation/transactions/${id}/quick`; }

  private get blocked() { return (this.busy && !this.saving) || this.uncertain; }
  // Local identity survives category/amount changes and serialized responses.
  // Local keys never cross HTTP; origins reference confirmed indexes, while
  // snapshot keys echo the server's authoritative row/valuation identity.
  private identify(card: Card) {
    for (const row of card.portions) row.key ??= this.nextPortionKey++;
    return card;
  }
  private hasDrafts() { return this.current?.accountDraft !== undefined || (this.current?.portions.some((row) => row.draft !== undefined || row.fresh) ?? false); }
  private rows(card: Card, snapshot = false) { return card.portions.filter((row) => !row.fresh).map((row) => ({ CategoryId: row.category_id, Amount: row.amount, Tags: row.tags ?? [], ...(snapshot ? { SnapshotKey: row.snapshot_key } : {}) })); }
  private changed() {
    if (!this.current || !this.confirmed) return false;
    // Removing/recreating otherwise equal rows is still a new valuation path.
    const lineageChanged = this.current.portions.filter((row) => !row.fresh).some((row, index) => row.key !== this.confirmed!.portions[index]?.key);
    return this.current.accountOverride !== undefined || lineageChanged || JSON.stringify(this.rows(this.current)) !== JSON.stringify(this.rows(this.confirmed));
  }

  private setBusy(busy: boolean) {
    this.busy = busy;
    this.element.setAttribute("aria-busy", String(busy));
    for (const button of this.element.querySelectorAll<HTMLButtonElement>("button")) {
      if (!this.editorContentTarget.contains(button)) button.disabled = button.matches('[data-action="quick-categorisation#cancelEditor"]') ? busy : this.blocked || (busy && button.matches("[data-quick-group], [data-quick-gesture]"));
    }
    for (const input of this.portionsTarget.querySelectorAll<HTMLInputElement>("input")) input.disabled = this.blocked;
    this.accountInputTarget.disabled = this.blocked;
    for (const link of this.element.querySelectorAll<HTMLElement>("[data-quick-leave]")) link.setAttribute("aria-disabled", String(busy));
    this.reloadTarget.disabled = busy;
    this.continueTarget.disabled = busy || !this.readBlocked;
    this.undoTarget.disabled = busy || this.uncertain || !this.undoStep;
    if (!busy && this.readBlocked) this.continueTarget.focus();
  }

  private async json(response: Response) {
    const value = await response.json();
    if (!response.ok) {
      if (["unavailable", "out_of_range", "needs_repair"].includes(value.code)) throw new QuickReadError(value.error, value.code);
      throw new Error(value.error ?? "Unable to load or save. Reload to check saved state.");
    }
    return value;
  }

  private setStatus(message: string, status: Status) {
    this.statusTarget.textContent = message;
    // Keep detailed, transaction-specific announcements without turning the
    // stationary dock into a second information panel. Drafts/errors stay full.
    this.feedbackTarget.textContent = ({
      loading: "Loading…", "loading-next": "Saved · Loading next…",
      saving: "Saving…", saved: "Saved", restoring: "Restoring…", restored: "Restored",
      ready: message === "Choose an uncategorised portion." ? "Ready" : message,
      draft: message, error: message,
    } satisfies Record<Status, string>)[status];
    this.workingTarget.textContent = ({ loading: message, "loading-next": "Saved · Loading next transaction…", saving: "Saving changes…", restoring: "Restoring transaction…" } as Partial<Record<Status, string>>)[status] ?? "";
    this.element.dataset.quickStatus = status;
    this.element.dataset.quickFeedback = status === "draft" || status === "error" || (status === "ready" && message !== "Choose an uncategorised portion.") ? "expanded" : "compact";
  }

  private focusDescription() {
    this.descriptionTarget.focus({ preventScroll: true });
    const bounds = this.descriptionTarget.getBoundingClientRect();
    const navigation = document.querySelector<HTMLElement>('[data-controller~="bottom-sheet"]')?.getBoundingClientRect();
    const bottom = navigation?.height ? Math.min(window.innerHeight, navigation.top) : window.innerHeight;
    const top = parseFloat(getComputedStyle(document.documentElement).fontSize) * 4.5;
    // Keep the ordinary portrait workspace steady, but reveal the new/restored
    // transaction when a short viewport or enlarged text required page scrolling.
    if (bounds.top < top || bounds.bottom > bottom) this.descriptionTarget.scrollIntoView({ block: "start", behavior: "instant" });
  }

  private fail(error: unknown, prefix = "") {
    if (!this.connected) return;
    this.uncertain = true;
    const message = error instanceof Error ? error.message : "Connection failed. Reload to check saved state before another change.";
    this.setStatus(`${prefix}${message}`, "error");
    this.reloadTarget.hidden = false;
  }

  private rememberUndo(saved: Card) {
    if (!saved.undo) return;
    this.undoStep = { id: saved.id, revision: saved.revision, receipt: saved.undo, description: saved.description || "Transaction" };
    this.undoTarget.hidden = false;
    this.undoTarget.setAttribute("aria-label", `Undo last save: ${this.undoStep.description}`);
    this.undoEditorTarget.href = `/categorisation/transactions/${saved.id}/categorise`;
    this.undoEditorTarget.hidden = true;
  }

  private invalidateUndo(card: Card) {
    if (this.undoStep?.id !== card.id || this.undoStep.revision === card.revision) return;
    this.undoStep = undefined;
    this.undoTarget.hidden = true;
    this.undoEditorTarget.hidden = true;
  }

  private showReadBlock(blocked: ReadBlock) {
    this.resetGesture();
    this.readBlocked = blocked;
    this.uncertain = true;
    // This explicit target read cannot safely expose/edit the old snapshot.
    // Clear its facts and local intents, never install an unrelated head here.
    this.current = undefined;
    this.confirmed = undefined;
    this.cardTarget.hidden = true;
    this.descriptionTarget.textContent = "";
    this.totalTarget.textContent = "";
    this.sourceTarget.textContent = "";
    this.metadataTarget.textContent = "";
    this.portionsTarget.replaceChildren();
    this.controlsTarget.hidden = true;
    this.directionsTarget.hidden = true;
    this.completionTarget.hidden = true;
    this.editorTarget.removeAttribute("href");
    this.editorBefore = undefined;
    this.editorDialogTarget.close();
    this.editorContentTarget.replaceChildren();
    if (this.undoStep?.id === blocked.id) {
      this.undoStep = undefined;
      this.undoTarget.hidden = true;
    }
    this.undoEditorTarget.hidden = true;
    this.reloadTarget.hidden = false;
    this.continueTarget.hidden = false;
    this.setStatus(`${blocked.error} The previous action will not be applied to another card.`, "error");
  }

  private async reconcileReadBlock(id: string, error: unknown) {
    if (!(error instanceof QuickReadError) || !this.connected) return false;
    this.showReadBlock({ id, code: error.code, error: error.message });
    // Count stays scoped to the original pass; the failed target is not replaced
    // by this read's unrelated head. Network failure does not authorize a retry.
    this.updateScope(await this.head());
    return true;
  }

  async continueWithoutTarget() {
    if (this.busy || !this.readBlocked) return;
    const blocked = this.readBlocked;
    if (blocked.code === "needs_repair") this.skipped.add(blocked.id);
    this.uncertainUndo = undefined;
    this.uncertainEditor = undefined;
    await this.loadHead(blocked.code === "needs_repair" ? "Left for later pending repair. Saved facts are unchanged." : "Unavailable transaction left behind. Review this card before choosing.");
  }

  private updateScope(data: { scope: string; count: number }) {
    this.scopeTarget.textContent = data.scope.replace(/^All accessible accounts/, "All accounts").replace(/^Selected accounts: /, "");
    this.countTarget.textContent = `${data.count} ${data.count === 1 ? "transaction" : "transactions"} to review`;
  }

  private async head() {
    const url = new URL(this.urlValue, window.location.origin);
    for (const id of this.skipped) url.searchParams.append("left_for_later", id);
    return this.json(await fetch(url, { headers: { Accept: "application/json" } }));
  }

  private async loadHead(feedback = "", saved?: Card, loadingMessage = "Loading transaction…") {
    this.resetGesture();
    const previousID = this.current?.id;
    const savedFeedback = feedback.startsWith("Saved:");
    this.setStatus(savedFeedback ? `${feedback} · Loading next transaction…` : loadingMessage, savedFeedback ? "loading-next" : "loading");
    this.setBusy(true);
    try {
      const data = await this.head();
      if (!this.connected) return;
      this.updateScope(data);
      if (data.blocked) { this.showReadBlock(data.blocked); return; }
      this.readBlocked = undefined;
      this.continueTarget.hidden = true;
      this.uncertain = false;
      this.reloadTarget.hidden = true;
      // Preserve submitted live order only for the same authoritative snapshot.
      // A newer owner/provider snapshot must not be replaced by an older save.
      const card = saved && data.card?.id === saved.id && data.card.revision === saved.revision ? saved : data.card;
      if (card) this.invalidateUndo(card);
      this.current = card ? this.identify(card) : undefined;
      this.confirmed = this.current ? structuredClone(this.current) : undefined;
      this.cardTarget.hidden = !this.current;
      this.controlsTarget.hidden = !this.current;
      this.directionsTarget.hidden = !this.current;
      this.completionTarget.hidden = !!this.current;
      if (this.current) {
        this.active = Math.max(0, this.current.portions.findIndex((portion) => this.pending(portion)));
        this.renderCard();
        if (this.current.id !== previousID) this.animateCardArrival();
      } else {
        this.completionTitleTarget.textContent = this.skipped.size ? `Finished for now — ${this.skipped.size} left for later` : "No more transactions to review in this scope";
        this.completionTitleTarget.focus();
      }
      const newer = saved && card?.id === saved.id && card.revision !== saved.revision;
      this.setStatus(newer ? "Newer saved state loaded. Review it before choosing again." : feedback || (this.current ? "Choose an uncategorised portion." : "Only this scope has been reviewed."), !newer && savedFeedback ? "saved" : "ready");
      // Leave initial entry unfocused. Later actions restore reading focus only
      // after the loading placeholder has left the workspace layout.
      if (this.current && (previousID || feedback)) this.focusDescription();
    } catch (error) { this.fail(error, savedFeedback ? `${feedback} · Saved, but the next transaction could not be loaded. ` : ""); }
    finally { if (this.connected) this.setBusy(false); }
  }

  private renderCard() {
    const card = this.current!;
    this.cardTarget.dataset.quickSingle = String(card.portions.length === 1);
    this.descriptionTarget.textContent = card.description || "Transaction";
    this.renderAccountAmount();
    this.sourceTarget.textContent = card.currency === card.account_currency ? "" : `Original transaction: ${card.amount} ${card.currency}. Portions are in ${card.currency}.`;
    this.metadataTarget.textContent = `${card.date} · ${card.account_name}`;
    this.editorTarget.href = `/categorisation/transactions/${card.id}/categorise`;
    this.portionsTarget.replaceChildren();
    card.portions.forEach((portion, index) => {
      const fragment = this.rowTemplateTarget.content.cloneNode(true) as DocumentFragment;
      const row = fragment.querySelector<HTMLElement>("[data-quick-row]")!;
      row.dataset.portion = String(index);
      row.dataset.quickCalculated = String(index === 0);
      const currency = row.querySelector<CurrencyInputElement>("currency-input")!;
      const input = row.querySelector<HTMLInputElement>("[data-quick-amount]")!;
      input.value = portion.amount;
      input.readOnly = index === 0;
      // Row 1 is a hidden, canonical state carrier behind the static amount.
      // Editable rows reuse the formatted currency control and its text keyboard.
      if (index === 0) currency.replaceWith(input);
      else currency.setAttribute("value", portion.amount);
      input.setAttribute("aria-label", `Portion ${index + 1} amount in ${card.currency}`);
      row.querySelector<HTMLElement>("[data-quick-currency]")!.textContent = card.currency;
      row.querySelector<HTMLElement>("[data-quick-calculated]")!.textContent = index === 0 ? "Calculated" : "";
      const remove = row.querySelector<HTMLButtonElement>("[data-quick-remove]")!;
      remove.hidden = card.portions.length < 2;
      const recipient = index === card.portions.length - 1 ? index - 1 : card.portions.length - 1;
      remove.setAttribute("aria-label", `Remove portion ${index + 1}; combine into portion ${recipient + 1}`);
      remove.title = `Remove / combine into portion ${recipient + 1}`;
      row.querySelector<HTMLElement>("[data-quick-tags]")!.textContent = (portion.tags ?? []).map((tag) => `#${tag}`).join(" ");
      this.portionsTarget.append(fragment);
      if (index > 0) currency.value = portion.amount;
      if (portion.draft !== undefined) input.value = portion.draft;
    });
    this.updateRows();
    this.setBusy(this.busy);
  }

  private renderAccountAmount() {
    const card = this.current!;
    const editable = card.currency !== card.account_currency;
    this.totalTarget.hidden = editable;
    this.accountTotalTarget.hidden = !editable;
    this.totalTarget.textContent = `${card.account_amount} ${card.account_currency}`;
    this.accountCurrencyTarget.textContent = card.account_currency;
    this.accountInputTarget.setAttribute("aria-label", `Account amount in ${card.account_currency}`);
    this.accountInputTarget.setAttribute("aria-invalid", String(card.accountDraft !== undefined && !parseDecimalAmount(card.accountDraft)));
    if (card.accountDraft !== undefined) this.accountInputTarget.value = card.accountDraft;
    else this.accountInputTarget.closest<CurrencyInputElement>("currency-input")!.value = card.accountOverride ?? card.account_amount;
    this.accountInputTarget.style.width = `${Math.max(1, this.accountInputTarget.value.length)}ch`;
  }

  accountAmountFocus() {
    if (this.blocked || !this.current || this.current.accountDraft !== undefined) return;
    this.accountInputTarget.value = new Decimal(this.current.accountOverride ?? this.current.account_amount).toFixed();
    this.accountInputTarget.style.width = `${Math.max(1, this.accountInputTarget.value.length)}ch`;
    this.accountInputTarget.select();
  }

  accountAmountInput() {
    if (this.blocked || !this.current) return;
    this.current.accountDraft = this.accountInputTarget.value;
    this.accountInputTarget.style.width = `${Math.max(1, this.accountInputTarget.value.length)}ch`;
    this.accountInputTarget.setAttribute("aria-invalid", String(!parseDecimalAmount(this.current.accountDraft)));
    this.setStatus("Unsaved account amount — Enter or leave the amount field to save.", "draft");
  }

  commitAccountAmount(event: Event) {
    if (event instanceof KeyboardEvent && (event.key !== "Enter" || event.isComposing)) return;
    if (event instanceof KeyboardEvent) event.preventDefault();
    if (this.blocked || !this.current) return;
    if (this.current.accountDraft === undefined) { this.renderAccountAmount(); return; }
    const amount = parseDecimalAmount(this.current.accountDraft);
    if (!amount) {
      this.setStatus("Unsaved account amount — enter a complete signed number.", "draft");
      return;
    }
    this.current.accountOverride = amount.toString();
    delete this.current.accountDraft;
    this.accountInputTarget.setAttribute("aria-invalid", "false");
    this.renderAccountAmount();
    this.feedback = `Saved: ${this.current.description} · Account amount`;
    void this.flush();
  }

  private balance() {
    const card = this.current!;
    card.portions[0].amount = card.portions.slice(1).reduce((total, row) => total.minus(row.amount), new Decimal(card.amount)).toString();
  }

  private updateRows() {
    const card = this.current!;
    const previewFirst = card.portions.slice(1).reduce((total, row) => total.minus(row.draft === undefined ? row.amount : parseDecimalAmount(row.draft) ?? row.amount), new Decimal(card.amount)).toString();
    card.portions.forEach((portion, index) => {
      const row = this.portionsTarget.querySelector<HTMLElement>(`[data-portion="${index}"]`)!;
      const color = this.paletteValue[portion.category_id];
      row.dataset.chartColor = color?.family ?? "neutral";
      row.dataset.chartShade = String(color?.shade ?? 0);
      row.dataset.quickCategorised = String(portion.category_id !== this.pendingValue);
      const amount = index === 0 ? previewFirst : portion.draft === undefined ? portion.amount : (parseDecimalAmount(portion.draft)?.toString() ?? portion.amount);
      const select = row.querySelector<HTMLElement>("[data-quick-select]")!;
      select.setAttribute("aria-label", `Portion ${index + 1}: ${this.name(portion)} · ${amount} ${card.currency}. Drag or use arrow keys to open a group's categories.`);
      select.title = this.name(portion);
      row.querySelector<HTMLElement>("[data-quick-category-label]")!.textContent = this.name(portion).split(" (")[0];
      row.querySelector<HTMLElement>("[data-quick-portion-amount]")!.textContent = ` · ${amount} ${card.currency}`;
      const input = row.querySelector<HTMLInputElement>("[data-quick-amount]")!;
      if (portion.draft === undefined) {
        const currency = row.querySelector<CurrencyInputElement>("currency-input");
        if (currency) currency.value = amount;
        else input.value = amount;
      }
      const calculated = row.querySelector<HTMLElement>("[data-quick-calculated-amount]")!;
      calculated.textContent = amount;
      row.querySelector<HTMLElement>("[data-quick-calculated-currency]")!.textContent = card.currency;
      input.style.width = `${Math.max(1, (input.value || input.placeholder).length)}ch`;
      calculated.setAttribute("aria-label", `Portion ${index + 1} amount: ${amount} ${card.currency}. Calculated from the other portions.`);
      input.setAttribute("aria-invalid", String(portion.draft !== undefined && portion.draft.trim() !== "" && !parseDecimalAmount(portion.draft)));
      row.querySelector<HTMLElement>("[data-quick-draft]")!.textContent = portion.fresh ? "Unsaved new portion — enter a nonzero amount or remove it" : portion.draft !== undefined ? "Unsaved amount draft" : "";
    });
    this.renderActive();
  }

  amountFocus(event: Event) {
    if (this.blocked || !this.current) return;
    const input = event.currentTarget as HTMLInputElement;
    const index = Number(input.closest<HTMLElement>("[data-quick-row]")!.dataset.portion);
    const row = this.current.portions[index];
    // Edit an ungrouped, exact number. Display grouping must not become an
    // ambiguous decimal mark, and unfinished drafts must survive refocusing.
    if (index > 0 && row.draft === undefined) {
      input.value = new Decimal(row.amount).toFixed();
      input.style.width = `${Math.max(1, (input.value || input.placeholder).length)}ch`;
      // Select the exact amount after switching formats, including for autofill.
      input.select();
    }
  }

  amountInput(event: Event) {
    if (this.blocked || !this.current) return;
    const input = event.currentTarget as HTMLInputElement;
    const index = Number(input.closest<HTMLElement>("[data-quick-row]")!.dataset.portion);
    if (index === 0) return;
    this.active = index;
    this.current.portions[index].draft = input.value;
    this.updateRows();
    this.setStatus("Unsaved changes — Enter or leave the amount field to save.", "draft");
  }

  commitAmount(event: Event) {
    if (event instanceof KeyboardEvent && (event.key !== "Enter" || event.isComposing)) return;
    if (event instanceof KeyboardEvent) event.preventDefault();
    if (this.blocked || !this.current) return;
    // Native change comes from the inner input; the formatted control also
    // emits a bubbling change from its host. Both share this one commit boundary.
    const target = event.target as HTMLElement;
    const input = target instanceof HTMLInputElement ? target : target.querySelector<HTMLInputElement>("[data-quick-amount]")!;
    const index = Number(input.closest<HTMLElement>("[data-quick-row]")!.dataset.portion);
    const row = this.current.portions[index];
    if (index === 0) return;
    if (row.draft === undefined) {
      // A focus-only edit is not save intent; restore the canonical display.
      input.closest<CurrencyInputElement>("currency-input")!.value = row.amount;
      return;
    }
    const amount = parseDecimalAmount(row.draft);
    if (!amount || (row.fresh && amount.isZero())) {
      this.setStatus("Unsaved changes — enter a complete number (nonzero for a new portion), or remove the new portion.", "draft");
      return;
    }
    row.amount = amount.toString();
    delete row.draft;
    delete row.fresh;
    this.balance();
    this.updateRows();
    this.feedback = `Saved: ${this.current.description} · Portion ${index + 1} amount`;
    void this.flush();
  }

  addPortion() {
    if (this.blocked || !this.current) return;
    this.resetGesture();
    this.current.portions.push({ category_id: this.pendingValue, amount: "0", tags: [], draft: "", fresh: true, key: this.nextPortionKey++ });
    this.active = this.current.portions.length - 1;
    this.renderCard();
    this.portionsTarget.querySelector<HTMLInputElement>(`[data-portion="${this.active}"] input`)!.focus();
    this.setStatus("Unsaved new portion — choose a category and enter a nonzero amount.", "draft");
  }

  removePortion(event: Event) {
    if (this.blocked || !this.current || this.current.portions.length < 2) return;
    const index = Number((event.currentTarget as HTMLElement).closest<HTMLElement>("[data-quick-row]")!.dataset.portion);
    const rows = this.current.portions;
    if (index === 0 && rows[1].draft !== undefined && !window.confirm("The next portion becomes calculated. Discard its unsaved amount draft?")) return;
    // Discard only the removed row's draft; all other surviving drafts stay visible.
    this.resetGesture();
    const [removed] = rows.splice(index, 1);
    const recipient = rows[rows.length - 1];
    recipient.amount = new Decimal(recipient.amount).plus(removed.amount).toString();
    if (!new Decimal(recipient.amount).isZero()) delete recipient.fresh;
    this.balance();
    if (index === 0) {
      delete rows[0].draft;
      delete rows[0].fresh;
    }
    this.active = Math.min(index, rows.length - 1);
    this.renderCard();
    this.portionsTarget.querySelector<HTMLButtonElement>(`[data-portion="${this.active}"] [data-quick-select]`)!.focus();
    this.feedback = `Saved: ${this.current.description} · Removed portion ${index + 1}; combined into portion ${rows.length}`;
    void this.flush();
  }

  private renderActive() {
    if (!this.current) return;
    this.activeTarget.hidden = this.current.portions.length === 1;
    this.activeTarget.textContent = `Selected: portion ${this.active + 1} · ${this.current.portions[this.active].amount} ${this.current.currency}`;
    this.gestureHintTarget.textContent = GESTURE_HINT;
    for (const row of this.portionsTarget.querySelectorAll<HTMLElement>("[data-quick-row]")) {
      const selected = Number(row.dataset.portion) === this.active;
      row.dataset.quickActive = String(selected);
      row.querySelector("[data-quick-select]")!.setAttribute("aria-pressed", String(selected));
      row.querySelector<HTMLElement>("[data-quick-portion-label]")!.textContent = `Portion ${Number(row.dataset.portion) + 1}`;
    }
  }

  selectPortion(event: Event) {
    if (this.blocked) return;
    this.active = Number((event.currentTarget as HTMLElement).closest<HTMLElement>("[data-quick-row]")!.dataset.portion);
    this.renderActive();
  }

  openGroup(event: Event) {
    if (!this.current || this.busy || this.uncertain) return;
    const opener = event.currentTarget as HTMLElement;
    this.showGroup(opener.dataset.quickGroup!, opener);
  }

  private showGroup(group: string, opener: HTMLElement) {
    if (!this.current || this.busy || this.uncertain) return;
    this.opener = opener;
    const dialog = this.element.querySelector<HTMLDialogElement>(`[data-quick-sheet="${group}"]`)!;
    const portion = this.current.portions[this.active];
    dialog.dataset.portion = String(this.active);
    dialog.querySelector<HTMLElement>("[data-quick-sheet-portion]")!.textContent = `Portion ${this.active + 1} · ${portion.amount} ${this.current.currency}`;
    dialog.showModal();
    dialog.querySelector<HTMLButtonElement>("[data-category]")!.focus();
  }

  closeGroup(event: Event) { (event.currentTarget as HTMLElement).closest("dialog")?.close(); }
  backdrop(event: Event) { if (event.target === event.currentTarget) (event.currentTarget as HTMLDialogElement).close(); }
  restoreFocus() { this.opener?.focus(); }

  choose(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const dialog = button.closest("dialog");
    const portion = dialog ? Number(dialog.dataset.portion) : this.active;
    dialog?.close();
    void this.saveCategory(portion, button.dataset.category!);
  }

  private async saveCategory(portion: number, categoryID: string) {
    if (!this.current || this.blocked) return;
    this.resetGesture();
    this.current.portions[portion].category_id = categoryID;
    this.updateRows();
    if (this.current.portions[portion].fresh) {
      this.setStatus(`Unsaved new portion ${portion + 1} → ${this.namesValue[categoryID]}. Enter a nonzero amount or remove it.`, "draft");
      return;
    }
    this.feedback = `Saved: ${this.current.description || "Transaction"} · Portion ${portion + 1} → ${this.namesValue[categoryID]}`;
    await this.flush();
  }

  private async flush() {
    if (this.flight) { await this.flight; return; }
    if (this.blocked || !this.current || !this.confirmed) return;
    this.flight = (async () => {
      await this.saveSnapshots();
      if (!this.connected || this.uncertain) return;
      if (!this.hasDrafts() && !this.changed() && this.current && !this.current.portions.some((row) => this.pending(row))) {
        await this.loadHead(this.feedback);
      }
    })();
    await this.flight;
    this.flight = undefined;
  }

  private async saveSnapshots() {
    this.saving = true;
    this.setBusy(true);
    try {
      while (this.changed()) {
        const before = this.confirmed!;
        const rows = this.rows(this.current!);
        const accountOverride = this.current!.accountOverride;
        const keys = this.current!.portions.filter((row) => !row.fresh).map((row) => row.key);
        const origins = keys.map((key) => before.portions.findIndex((row) => row.key === key));
        this.setStatus(this.feedback.replace(/^Saved:/, "Saving:"), "saving");
        const saved: Card = await this.json(await fetch(this.endpoint(before.id), {
          method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.element.querySelector<HTMLInputElement>('input[name="csrf_token"]')!.value },
          body: JSON.stringify({ revision: before.revision, portions: this.rows(before, true), split: rows, origins, exact_converted_total: accountOverride }),
        }));
        if (!this.connected) return;
        saved.portions.forEach((row, index) => { row.key = keys[index]; });
        this.confirmed = saved;
        this.rememberUndo(saved);
        // Never replace local rows/inputs with an older response: edits may have
        // continued while this full balanced snapshot was in flight.
        this.current!.revision = saved.revision;
        this.current!.account_amount = saved.account_amount;
        if (this.current!.accountOverride === accountOverride) delete this.current!.accountOverride;
        this.renderAccountAmount();
      }
      this.setStatus(this.hasDrafts() ? `${this.feedback} · Unsaved amount/new-portion draft remains.` : this.feedback, this.hasDrafts() ? "draft" : "saved");
      if (!this.hasDrafts() && this.current) {
        const next = this.current.portions.findIndex((row) => this.pending(row));
        if (next >= 0) { this.active = next; this.renderActive(); }
      }
    } catch (error) { this.fail(error, "Save could not be confirmed. "); }
    finally { this.saving = false; if (this.connected) this.setBusy(false); }
  }

  private async resolvePending() {
    if (this.readBlocked) return !this.busy; // Its discarded old snapshot cannot be flushed.
    await this.flight;
    if (this.changed()) await this.flush();
    if (this.uncertain) return false;
    if (this.hasDrafts()) {
      if (!window.confirm("Discard unsaved amount drafts and unfinished new portions? Already saved changes will remain.")) return false;
      this.current = structuredClone(this.confirmed!);
      this.active = Math.min(this.active, this.current.portions.length - 1);
      this.renderCard();
    }
    return !this.busy;
  }

  async undo() {
    if (this.busy || this.uncertain || this.undoing || !this.undoStep) return;
    this.undoing = true;
    try {
      if (!(await this.resolvePending()) || !this.undoStep) return;
      const step = this.undoStep; // Pending saves may have replaced the one-step receipt.
      this.resetGesture();
      this.setBusy(true);
      this.setStatus(`Restoring: ${step.description}…`, "restoring");
      const restored: Card = await this.json(await fetch(this.endpoint(step.id), {
        method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.element.querySelector<HTMLInputElement>('input[name="csrf_token"]')!.value },
        body: JSON.stringify({ undo: step.receipt }),
      }));
      if (!this.connected) return;
      this.undoStep = undefined; // One step, no redo or Undo of the Undo itself.
      this.undoTarget.hidden = true;
      this.undoEditorTarget.hidden = true;
      this.skipped.delete(restored.id);
      this.current = this.identify(restored);
      this.confirmed = structuredClone(restored);
      this.cardTarget.hidden = false;
      this.controlsTarget.hidden = false;
      this.directionsTarget.hidden = false;
      this.completionTarget.hidden = true;
      this.active = Math.max(0, restored.portions.findIndex((row) => this.pending(row)));
      this.renderCard();
      this.animateCardArrival();
      this.focusDescription();
      this.setStatus(`Restored: ${step.description} · Complete preceding portions and account valuation intent.`, "restored");
      // The next card was never mutated or removed. Refresh the scoped count,
      // but keep the restored card at the front even if it was already complete.
      const data = await this.head();
      if (this.connected) this.updateScope(data);
    } catch (error) {
      this.fail(error);
      if (this.connected && this.undoStep) {
        this.uncertainUndo = this.undoStep.id;
        this.setStatus(`Undo could not be confirmed: ${this.undoStep.description}. ${this.statusTarget.textContent}`, "error");
        this.undoEditorTarget.hidden = false;
      }
    } finally { this.undoing = false; if (this.connected) this.setBusy(false); }
  }

  async openEditor(event: Event) {
    event.preventDefault();
    const link = event.currentTarget as HTMLAnchorElement;
    const url = link.href; // Awaited saves may advance and retarget this live link.
    const id = new URL(url).pathname.split("/")[3];
    if (this.busy && !this.saving) return;
    if (this.uncertain) {
      // Opening the offered editor is an explicit authoritative read, not a
      // retry. Reconcile the failed target before accepting any editor write.
      await this.reloadCard();
      if (this.uncertain) return;
    } else if (!(await this.resolvePending())) return;
    this.resetGesture();
    this.setBusy(true);
    try {
      const before: Card = await this.json(await fetch(this.endpoint(id), { headers: { Accept: "application/json" } }));
      const response = await fetch(url);
      if (!response.ok) throw new Error("Unable to open the detailed editor. Reload saved state.");
      const html = await response.text();
      if (!this.connected) return;
      this.invalidateUndo(before);
      this.editorBefore = this.confirmed?.id === id && this.confirmed.revision === before.revision ? structuredClone(this.confirmed) : before;
      if (this.current?.id === id && this.confirmed?.revision !== before.revision) {
        // Cancel must return to the newly read target, not the stale card that
        // opened the editor. Never rebase local edits onto someone else's rows.
        this.current = this.identify(structuredClone(before));
        this.confirmed = structuredClone(this.current);
        this.active = Math.max(0, before.portions.findIndex((portion) => this.pending(portion)));
        this.renderCard();
        this.updateScope(await this.head());
        if (!this.connected) return;
        this.setStatus("Newer saved state loaded. Review it before choosing again.", "ready");
      }
      this.installEditor(html);
      this.opener = link;
      this.editorStatusTarget.textContent = "Edit portions, tags and account amount. Cancel leaves saved facts unchanged.";
      this.editorDialogTarget.showModal();
    } catch (error) {
      try { if (!(await this.reconcileReadBlock(id, error))) this.fail(error); }
      catch (error) { this.fail(error); }
    }
    finally { if (this.connected) this.setBusy(false); }
  }

  private installEditor(html: string) {
    const document = new DOMParser().parseFromString(html, "text/html");
    const form = document.querySelector<HTMLFormElement>('form[data-controller~="categorise"]');
    if (!form) throw new Error("Unable to read the detailed editor. Reload saved state.");
    // Reuse the existing form/row/category/currency controllers, but leave
    // submission and mode lifecycle with this deck's confirmed-save boundary.
    form.dataset.controller = "categorise";
    form.dataset.turbo = "false";
    form.querySelector<HTMLAnchorElement>('[data-categorisation-cancel]')!.dataset.action = "quick-categorisation#cancelEditor";
    this.editorContentTarget.replaceChildren(document.importNode(form, true));
  }

  cancelEditor(event: Event) {
    if (event.type !== "close") event.preventDefault();
    if (this.busy) return;
    this.editorDialogTarget.close();
    this.editorContentTarget.replaceChildren();
    this.editorBefore = undefined;
    this.restoreFocus();
  }

  private async saveEditor(form: HTMLFormElement) {
    if (this.busy || this.uncertain || !this.editorBefore) return;
    const body = new FormData(form);
    body.set("quick_revision", this.editorBefore.revision);
    body.set("quick_portions", JSON.stringify(this.rows(this.editorBefore, true)));
    this.setBusy(true);
    this.editorContentTarget.inert = true;
    this.editorStatusTarget.textContent = "Saving…";
    try {
      const response = await fetch(form.action, { method: "POST", headers: { Accept: "application/json" }, body });
      if (!(response.headers.get("Content-Type") ?? "").includes("application/json")) {
        const html = await response.text();
        if (!this.connected) return;
        if (response.status !== 400) throw new Error("Editor save could not be confirmed. Close the editor and reload saved state.");
        this.installEditor(html);
        this.editorStatusTarget.textContent = "Not saved — correct the form errors, or Cancel to return unchanged.";
        return;
      }
      const saved: Card = await this.json(response);
      if (!this.connected) return;
      this.rememberUndo(saved);
      this.editorDialogTarget.close();
      this.editorContentTarget.replaceChildren();
      this.editorBefore = undefined;
      // Re-evaluate full eligibility, including search tags, with this pass's
      // Skip exclusions. Retain submitted row order if the card still matches.
      await this.loadHead(`Saved: ${saved.description} · Detailed editor`, saved);
    } catch (error) {
      this.uncertainEditor = this.editorBefore?.id;
      this.fail(error);
      if (this.connected) this.editorStatusTarget.textContent = `Save could not be confirmed. ${this.statusTarget.textContent} Close the editor and reload saved state before another change.`;
    } finally {
      if (this.connected) { this.editorContentTarget.inert = false; this.setBusy(false); }
    }
  }

  private async leave(url: string) { if (await this.resolvePending()) window.location.assign(url); }
  async skip() {
    const id = this.current?.id;
    if (!id || !(await this.resolvePending())) return;
    this.skipped.add(id);
    await this.loadHead("Left for later. Saved categorisation is unchanged.", undefined, "Skipping · Loading next transaction…");
  }
  newPass() { if (!this.busy && !this.uncertain) { this.skipped.clear(); void this.loadHead(); } }
  async reloadCard() {
    if (this.busy) return;
    const id = this.readBlocked?.id ?? this.uncertainUndo ?? this.uncertainEditor ?? this.current?.id;
    if (!id) { await this.loadHead(); return; }
    if ((this.hasDrafts() || this.changed()) && !window.confirm("Reload authoritative saved state and discard unsaved local edits? An uncertain write may already have saved.")) return;
    this.setBusy(true);
    try {
      const target: Card = await this.json(await fetch(this.endpoint(id), { headers: { Accept: "application/json" } }));
      const reconcilingUndo = !!this.uncertainUndo;
      if (reconcilingUndo) this.skipped.delete(id);
      const data = await this.head();
      if (!this.connected) return;
      // Refresh count/scope on every reconciliation, but never install the next
      // card in place of the failed target. If head re-read this same target,
      // use that later authoritative snapshot rather than the earlier GET.
      const saved: Card = data.card?.id === id ? data.card : target;
      this.updateScope(data);
      this.invalidateUndo(saved);
      this.current = this.identify(saved);
      this.confirmed = structuredClone(saved);
      this.active = Math.max(0, saved.portions.findIndex((portion) => this.pending(portion)));
      this.uncertainUndo = undefined;
      this.uncertainEditor = undefined;
      this.readBlocked = undefined;
      this.continueTarget.hidden = true;
      this.uncertain = false;
      this.reloadTarget.hidden = true;
      this.undoEditorTarget.hidden = true;
      this.cardTarget.hidden = false;
      this.controlsTarget.hidden = false;
      this.directionsTarget.hidden = false;
      this.completionTarget.hidden = true;
      this.renderCard();
      this.setStatus("Saved state reloaded. Review it before choosing again.", "ready");
      this.focusDescription();
    } catch (error) {
      try { if (!(await this.reconcileReadBlock(id, error))) this.fail(error); }
      catch (error) { this.fail(error); }
    }
    finally { if (this.connected) this.setBusy(false); }
  }

  private animateCardArrival() {
    this.cardArrival?.cancel();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    this.cardArrival = this.cardTarget.animate([{ opacity: 0.65, transform: "translateY(6px)" }, { opacity: 1, transform: "translateY(0)" }], { duration: 160, easing: "cubic-bezier(0, 0, 0.2, 1)" });
  }

  // Drag rendering is frame-batched and never decides when a save completes.
  // Preview begins early, leaving room to return to neutral before committing.
  private present(gesture: Gesture, dx: number, dy: number) {
    const group = this.direction(dx, dy, 12);
    const ready = !!this.direction(dx, dy);
    const state = group ? ready ? "ready" : "preview" : this.gesture ? "dragging" : "idle";
    gesture.visual.dataset.quickGestureState = state;
    const groupButton = group ? this.element.querySelector<HTMLElement>(`[data-quick-group="${group}"]`)! : undefined;
    if (ready && groupButton) gesture.visual.style.setProperty("--budgeter-quick-drag-color", getComputedStyle(groupButton).getPropertyValue("--budgeter-chart-color"));
    else gesture.visual.style.removeProperty("--budgeter-quick-drag-color");
    const label = group
      ? `${ready ? "Release" : "Keep dragging"} → Choose category · ${groupButton!.dataset.label}`
      : this.gesture ? "Choose a clear direction" : "Drag to categorise";
    for (const surface of [gesture.surface, gesture.previewSurface]) {
      if (!surface) continue;
      surface.dataset.quickGestureState = state;
      surface.dataset.quickDirection = group ?? "";
      surface.querySelector<HTMLElement>("[data-quick-direction]")!.textContent = label;
    }
    this.gestureHintTarget.textContent = this.gesture ? label : GESTURE_HINT;
    if (gesture.previewLabel) gesture.previewLabel.textContent = label;
    // Readiness is only feedback: movement keeps following the pointer 1:1
    // until release, including beyond the commit boundary and on the way back.
    gesture.visual.style.setProperty("--budgeter-quick-drag-x", `${dx}px`);
    gesture.visual.style.setProperty("--budgeter-quick-drag-y", `${dy}px`);
    for (const button of this.element.querySelectorAll<HTMLElement>("[data-quick-group]")) {
      button.dataset.quickPreview = String(button.dataset.quickGroup === group);
      button.dataset.quickReady = String(ready && button.dataset.quickGroup === group);
      button.dataset.quickSelector = String(ready && button.dataset.quickGroup === group);
      button.dataset.quickTargetLabel = button.dataset.quickGroup === group ? label : button.dataset.label!;
    }
  }

  private direction(dx: number, dy: number, threshold = DRAG_COMMIT_DISTANCE) {
    const x = Math.abs(dx), y = Math.abs(dy);
    if (Math.max(x, y) < threshold) return undefined;
    if (x > y * 1.3) return dx < 0 ? "recurring" : "everyday";
    if (y > x * 1.3) return dy < 0 ? "income" : "savings";
    return undefined;
  }

  startCardGesture(event: PointerEvent) {
    if (this.current?.portions.length !== 1) return;
    const target = event.target as Element;
    if (target.closest("button, a, input, select, textarea, [data-quick-scroll]")) return;
    if (!target.closest("[data-quick-card-background], [data-quick-card-drag]")) return;
    this.beginGesture(event, this.portionsTarget.querySelector<HTMLElement>("[data-quick-gesture]")!);
  }

  startGesture(event: PointerEvent) {
    this.beginGesture(event, event.currentTarget as HTMLElement);
  }

  private beginGesture(event: PointerEvent, surface: HTMLElement) {
    if (this.gesture) { this.resetGesture(); return; }
    if (!this.current || this.busy || this.uncertain || event.button !== 0 || !event.isPrimary) return;
    const row = surface.closest<HTMLElement>("[data-quick-row]")!;
    const portion = Number(row.dataset.portion);
    this.active = portion;
    this.renderActive();
    this.cardArrival?.cancel();
    this.suppressClick = false;
    let visual = this.cardTarget;
    let previewSurface: HTMLElement | undefined;
    let previewLabel: HTMLElement | undefined;
    if (this.current.portions.length > 1) {
      const bounds = row.getBoundingClientRect();
      visual = row.cloneNode(true) as HTMLElement;
      // A visual copy must not reconnect custom inputs or format a retained draft.
      for (const currency of visual.querySelectorAll("currency-input")) {
        const wrapper = document.createElement("div");
        wrapper.className = currency.className;
        wrapper.append(...currency.childNodes);
        currency.replaceWith(wrapper);
      }
      previewSurface = visual.querySelector<HTMLElement>("[data-quick-gesture]")!;
      const label = previewSurface.querySelector<HTMLElement>("[data-quick-direction]")!;
      // The clone is presentation only: no Stimulus targets/actions, row identity
      // or focusable controls may join the live transaction's DOM contract.
      for (const element of [visual, ...visual.querySelectorAll<HTMLElement>("*")]) {
        for (const attribute of [...element.attributes]) if (attribute.name.startsWith("data-") || attribute.name === "id") element.removeAttribute(attribute.name);
      }
      label.setAttribute("data-quick-direction", "");
      visual.classList.add("budgeter-quick__drag-preview");
      visual.setAttribute("data-quick-drag-preview", "");
      visual.style.setProperty("--budgeter-quick-category-color", getComputedStyle(row).getPropertyValue("--budgeter-chart-color"));
      visual.dataset.quickCategorised = row.dataset.quickCategorised;
      visual.dataset.quickActive = "true";
      visual.dataset.quickCalculated = row.dataset.quickCalculated;
      const columns = getComputedStyle(row).gridTemplateColumns;
      visual.style.gridTemplateColumns = columns;
      visual.dataset.quickStacked = String(columns.split(" ").length === 2);
      visual.setAttribute("aria-hidden", "true");
      visual.inert = true;
      previewLabel = document.createElement("p");
      previewLabel.className = "budgeter-quick__drag-caption";
      visual.append(previewLabel);
      Object.assign(visual.style, { left: `${bounds.left}px`, top: `${bounds.top}px`, width: `${bounds.width}px`, height: `${bounds.height}px` });
      document.body.append(visual);
      this.previews.add(visual);
    }
    visual.dataset.quickDragging = "true";
    this.gesture = { pointer: event.pointerId, surface, visual, previewSurface, previewLabel, portion, x: event.clientX, y: event.clientY, dx: 0, dy: 0 };
    this.present(this.gesture, 0, 0);
    surface.setPointerCapture(event.pointerId);
  }

  moveGesture(event: PointerEvent) {
    const gesture = this.gesture;
    if (!gesture || gesture.pointer !== event.pointerId) return;
    gesture.dx = event.clientX - gesture.x;
    gesture.dy = event.clientY - gesture.y;
    if (Math.max(Math.abs(gesture.dx), Math.abs(gesture.dy)) > 10) this.suppressClick = true;
    if (this.gestureFrame !== undefined) return;
    this.gestureFrame = window.requestAnimationFrame(() => {
      this.gestureFrame = undefined;
      if (this.gesture === gesture) this.present(gesture, gesture.dx, gesture.dy);
    });
  }

  endGesture(event: PointerEvent) {
    const gesture = this.gesture;
    if (!gesture || gesture.pointer !== event.pointerId) return;
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
    const group = this.direction(dx, dy);
    this.resetGesture();
    if (group) {
      this.active = gesture.portion;
      this.showGroup(group, gesture.surface);
    }
  }
  resetGesture() {
    if (this.gestureFrame !== undefined) window.cancelAnimationFrame(this.gestureFrame);
    this.gestureFrame = undefined;
    const gesture = this.gesture;
    this.gesture = undefined;
    if (!gesture) return;
    const from = `${gesture.visual.style.getPropertyValue("--budgeter-quick-drag-x")} ${gesture.visual.style.getPropertyValue("--budgeter-quick-drag-y")}`;
    delete gesture.visual.dataset.quickDragging;
    this.present(gesture, 0, 0);
    if (gesture.previewSurface) {
      const remove = () => { gesture.visual.remove(); this.previews.delete(gesture.visual); };
      if (this.connected && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        const animation = gesture.visual.animate([{ translate: from, opacity: 1 }, { translate: "0px 0px", opacity: 0 }], { duration: 160, easing: "cubic-bezier(0, 0, 0.2, 1)" });
        animation.onfinish = remove;
        animation.oncancel = remove;
      } else remove();
    }
    if (gesture.surface.hasPointerCapture(gesture.pointer)) gesture.surface.releasePointerCapture(gesture.pointer);
  }
  cancelGesture(event: KeyboardEvent) { if (event.key === "Escape") this.resetGesture(); }
  gestureClick(event: Event) { if (this.suppressClick) { event.preventDefault(); this.suppressClick = false; } else this.selectPortion(event); }
  directionKey(event: KeyboardEvent) {
    const group = ({ ArrowLeft: "recurring", ArrowRight: "everyday", ArrowUp: "income", ArrowDown: "savings" } as Record<string, string>)[event.key];
    if (!group || this.gesture) return;
    event.preventDefault();
    const opener = event.currentTarget as HTMLElement;
    if (this.blocked) return;
    this.active = Number(opener.closest<HTMLElement>("[data-quick-row]")!.dataset.portion);
    this.renderActive();
    this.showGroup(group, opener);
  }
}
